import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  ErrorModal,
  OrderGroup,
  SearchBox,
  SelectControllerVertical,
  SuccessModal,
} from "@components";
import { Table } from "@pentabd/ui";
import { TableColumn } from "./TableColumn";

import { initiateTransfer, interchangeTransfer } from "@api/transferCandidates";

import { useTransferCandidates } from "@hooks/HRM/employees/useTransferCandidates";
import { useLanguage } from "@hooks/useLanguage";
import { useBaseData } from "@hooks/useBaseData";

import { TablePagination } from "@helpers/tablePagination";
import {
  getConvertedPositionData,
  getConvertedUnitData,
  getConvertedOfficeAddress,
} from "@helpers/baseDataConvert";

import { InitiateTransferType } from "@type/HRM/transfer-candidates";

import { ROUTES } from "@constants/routes";
import { BASE_DATA } from "@constants/base-data";

const { DESIGNATIONS, WINGS, OFFICE } = BASE_DATA;

const {
  HRM: { TRANSFERS, INTERCHANGE_TRANSFERS },
} = ROUTES;

export const TransferCandidatesTable = () => {
  const [activeStatus, setActiveStatus] = useState<number | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [isInterchange, setIsInterchange] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { language } = useLanguage();
  const { data: positions } = useBaseData(DESIGNATIONS);
  const { data: wings } = useBaseData(WINGS);
  const { data: office } = useBaseData(OFFICE);

  const [isLoading, setIsLoading] = useState(false);

  const { control, register, setValue } = useForm();

  const {
    employees,
    pagination,
    employeeRequest,
    handleQuery,
    handleOrder,
    queryParams,
  } = useTransferCandidates();
  const { total } = pagination || {};

  const orderGroups = [
    {
      value: "gradeOrder",
      label: t("SENIORITY_LIST.ORDERS.EMPLOYEE_GRADE"),
    },
    {
      value: "joiningDateOrder",
      label: t("SENIORITY_LIST.ORDERS.JOINING_DATE"),
    },
    {
      value: "promotionOrder",
      label: t("SENIORITY_LIST.ORDERS.PROMOTION_DATE"),
    },
  ];

  const status = orderGroups.map((item) => item.value);

  const ordGroupAction = (index: number) => {
    let statusId = 0;
    switch (index) {
      case 0:
        statusId = 0;
        break;
      case 1:
        statusId = 1;
        break;
      case 2:
        statusId = 2;
        break;
      default:
        statusId = 0;
        break;
    }

    setActiveStatus(statusId);
    handleOrder(status[statusId]);
  };

  const HandleInitiateTransfer = (ids: InitiateTransferType) => {
    setIsLoading(true);
    initiateTransfer(ids, (response) => {
      if (response.status === "success") {
        setIsSuccessModalOpen(true);
      } else {
        setIsErrorModalOpen(true);
      }
      setIsLoading(false);
    });
  };

  const interchangeInitiateTransfer = () => {
    setIsLoading(true);
    interchangeTransfer(
      {
        employeeId: selectedIds as number[],
      },
      (response) => {
        if (response.status === "success") {
          setIsSuccessModalOpen(true);
          setIsInterchange(true);
        } else {
          setIsErrorModalOpen(true);
        }
        setIsLoading(false);
      },
    );
  };

  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
    if (isInterchange) {
      setIsInterchange(false);
      setSelectedIds([]);
      navigate(`${INTERCHANGE_TRANSFERS}`);
    } else {
      navigate(`${TRANSFERS}`);
    }
  };
  const closeErrorModal = () => setIsErrorModalOpen(false);

  return (
    <div className="mt-10">
      {/* AFTER EFFECTS MODAL */}
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        subtitle="Transfer initiated successfully"
        action={closeSuccessModal}
      />
      <ErrorModal isModalOpen={isErrorModalOpen} closeModal={closeErrorModal} />
      {/* AFTER EFFECTS MODAL */}

      <Table
        loading={employeeRequest}
        header={{
          badge: {
            label: `${total || "0"}`,
            size: "sm",
            type: "primary",
          },
          title: t("TRANSFER_CANDIDATES.TABLE.HEADER.LABEL"),
          buttons: [
            <SearchBox
              key="search"
              control={control}
              handleQuery={handleQuery}
            />,
          ],
        }}
        columns={TableColumn(t, HandleInitiateTransfer)}
        rows={employees}
        headerExtension={{
          leftComponents: [
            <OrderGroup
              key="btn-group"
              orderGroupData={orderGroups}
              activeState={activeStatus}
              action={ordGroupAction}
              query={queryParams}
            />,
          ],
          rightComponents: [
            <div className="row promotion-candidate-list">
              <SelectControllerVertical
                control={control}
                label="PROMOTION_CANDIDATES.TABLE.FORM.OFFICE"
                name="officeId"
                options={getConvertedOfficeAddress(office, language)}
                register={register}
                setValue={setValue}
                isSearchable
                selectAction={(e) => {
                  handleQuery("officeId", e);
                }}
                portal
                cols={4}
                withoutLabel
              />

              <SelectControllerVertical
                control={control}
                label="PROMOTION_CANDIDATES.TABLE.FORM.DESIGNATION"
                name="positionId"
                options={getConvertedPositionData(positions, language)}
                register={register}
                setValue={setValue}
                isSearchable
                selectAction={(e) => {
                  handleQuery("positionId", e);
                }}
                portal
                cols={4}
                withoutLabel
              />

              <SelectControllerVertical
                control={control}
                label="PROMOTION_CANDIDATES.TABLE.FORM.UNIT"
                name="unitId"
                options={getConvertedUnitData(
                  wings.filter((item: any) => item?.name !== "ACC"),
                  language,
                )}
                register={register}
                setValue={setValue}
                isSearchable
                selectAction={(e) => {
                  handleQuery("unitId", e);
                }}
                portal
                cols={4}
                withoutLabel
              />
            </div>,
          ],
        }}
        check={{
          onCheck(ids) {
            setSelectedIds(ids);
          },
          onClickDelete: (ids) => {
            if (selectedIds.length === 2) {
              interchangeInitiateTransfer();
            } else {
              HandleInitiateTransfer(ids as any); //!!! UI library accepts both string[] and number[] but we have to provide number[]. so, used any
            }
          },
        }}
        pagination={TablePagination(pagination, handleQuery)}
        selectButtonLabel={
          selectedIds.length === 2
            ? t("TRANSFER_CANDIDATES.TABLE.HEADER.INTERCHANGE_BUTTON")
            : t("TRANSFER_CANDIDATES.TABLE.HEADER.INITIATE_BUTTON")
        }
        selectButtonProps={{
          size: "sm",
          type: "primary",
          loading: isLoading,
        }}
      />
    </div>
  );
};
